import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { route } from 'preact-router';

import { computeClubStepStates, countDone } from '../../lib/clubGettingStartedSteps';

// Static step/group definitions for the shooting-club «Kom i gang» card.
// Module-level JSX constants are the same pattern as lib/fields.js and the
// Dyrejournal card — read-only; never mutate.
const STEPS = [
    {
        key: 'frontpageArticle',
        icon: 'fa-duotone fa-newspaper',
        drawer: 'newArticle',
        title: <Text id='clubOps.gettingStarted.frontpageArticle.title'>Write the first frontpage article</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.frontpageArticle.line1'>Click «Take me there» below – the form for a new frontpage article opens.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.frontpageArticle.line2'>Write a welcome text about the club: who you are, where you shoot and how to become a member.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.frontpageArticle.line3'>Save – the article is shown to everyone who visits the club site.</Text>,
        ],
    },
    {
        key: 'feedPost',
        icon: 'fa-duotone fa-comments',
        href: '/feed',
        title: <Text id='clubOps.gettingStarted.feedPost.title'>Post the first update in the feed</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.feedPost.line1'>Open «Feed» in the bottom bar.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.feedPost.line2'>Write a short post with a photo from the range or the latest training.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.feedPost.line3'>Members see the post in the feed and can like and comment.</Text>,
        ],
    },
    {
        key: 'training',
        icon: 'fa-duotone fa-bullseye-pointer',
        href: '/convention',
        title: <Text id='clubOps.gettingStarted.training.title'>Create your first training</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.training.line1'>Open «Stevner og treninger» and click the green + button.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.training.line2'>Choose «Training» in the wizard, set the title, date and time, and save.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.training.line3'>Members register themselves at the range with the QR code or the 6-digit code shown to the admin.</Text>,
        ],
    },
    {
        key: 'competition',
        icon: 'fa-duotone fa-trophy',
        href: '/convention',
        title: <Text id='clubOps.gettingStarted.competition.title'>Create your first competition</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.competition.line1'>Click the green + button on «Stevner og treninger» again.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.competition.line2'>Choose «Competition» (or «Norgesfelt») in the wizard and fill in title, date and signup period.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.competition.line3'>Open the registration when you are ready – shooters sign up themselves.</Text>,
        ],
    },
    {
        key: 'results',
        icon: 'fa-duotone fa-ranking-star',
        href: '/convention',
        title: <Text id='clubOps.gettingStarted.results.title'>Enter results on a convention</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.results.line1'>Open the convention and go to the scoring view.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.results.line2'>Enter the scores per shooter and stage – the result list and ranking lists update live.</Text>,
        ],
    },
    {
        key: 'policeForm',
        icon: 'fa-duotone fa-bullseye-arrow',
        href: '/vapensoknad',
        title: <Text id='clubOps.gettingStarted.policeForm.title'>Try the weapons application wizard</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.policeForm.line1'>Open «Våpensøknad» – the wizard builds the police attachments from club data: membership card, start card, trainings and competitions.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.policeForm.line2'>Go through the steps and send the forms to the club for signing.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.policeForm.line3'>Tell the members about it – each member fills in their own application.</Text>,
        ],
    },
    {
        key: 'policeFormSigned',
        icon: 'fa-duotone fa-signature',
        href: '/signing-requests',
        title: <Text id='clubOps.gettingStarted.policeFormSigned.title'>Sign the first police form</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.policeFormSigned.line1'>Open «Signing requests» – forms sent by members are listed here.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.policeFormSigned.line2'>Check the form and click approve – or reject it with a reason.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.policeFormSigned.line3'>The member downloads the signed PDF and attaches it to the application to the police.</Text>,
        ],
    },
    {
        key: 'calendar',
        icon: 'fa-duotone fa-calendar-plus',
        drawer: 'newCalendar',
        title: <Text id='clubOps.gettingStarted.calendar.title'>Create a public club calendar</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.calendar.line1'>Give the calendar a name, e.g. «Klubbkalender».</Text>,
            <Text key='line2' id='clubOps.gettingStarted.calendar.line2'>Turn on «Public calendar» so the events show on the front page and to visitors.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.calendar.line3'>Save – the calendar is now ready for events.</Text>,
        ],
    },
    {
        key: 'calendarEvent',
        icon: 'fa-duotone fa-calendar-day',
        href: '/calendar/',
        title: <Text id='clubOps.gettingStarted.calendarEvent.title'>Add your first calendar event</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.calendarEvent.line1'>Open the calendar and click a day or time slot.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.calendarEvent.line2'>Fill in title, time and the calendar it belongs to, and save.</Text>,
        ],
    },
    {
        key: 'weapons',
        icon: 'fa-solid fa-gun',
        href: '/klubbdrift/vapen',
        title: <Text id='clubOps.gettingStarted.weapons.title'>Register the club weapons</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.weapons.line1'>Open «Klubbvåpen» under Klubbdrift and click «Nytt klubbvåpen».</Text>,
            <Text key='line2' id='clubOps.gettingStarted.weapons.line2'>Fill in make, model, caliber and serial number, and save.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.weapons.line3'>Loans, cleaning and maintenance are logged on each weapon.</Text>,
        ],
    },
    {
        key: 'ranges',
        icon: 'fa-solid fa-bullseye',
        href: '/klubbdrift/baner',
        title: <Text id='clubOps.gettingStarted.ranges.title'>Register the ranges</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.ranges.line1'>Open «Baner» under Klubbdrift and click «Ny bane».</Text>,
            <Text key='line2' id='clubOps.gettingStarted.ranges.line2'>Fill in the name and location, and save.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.ranges.line3'>Range inspections are logged per range.</Text>,
        ],
    },
    {
        key: 'rangeOfficers',
        icon: 'fa-solid fa-user-shield',
        href: '/klubbdrift/roller',
        title: <Text id='clubOps.gettingStarted.rangeOfficers.title'>Appoint range officers</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.rangeOfficers.line1'>Open «Roller» under Klubbdrift.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.rangeOfficers.line2'>Search for a member by name or mobile under «Utnevn et medlem».</Text>,
            <Text key='line3' id='clubOps.gettingStarted.rangeOfficers.line3'>Click «Gjør til skyteleder» – range officers can register ammo sales and club operations.</Text>,
        ],
    },
    {
        key: 'keys',
        icon: 'fa-solid fa-key',
        href: '/klubbdrift/nokler',
        title: <Text id='clubOps.gettingStarted.keys.title'>Register the club keys</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.keys.line1'>Open «Nøkler» under Klubbdrift and click «Ny nøkkel».</Text>,
            <Text key='line2' id='clubOps.gettingStarted.keys.line2'>Fill in the key label and who holds it – the holder confirms the key on their phone.</Text>,
        ],
    },
    {
        key: 'ammoSale',
        icon: 'fa-solid fa-box-open',
        href: '/ammosales',
        title: <Text id='clubOps.gettingStarted.ammoSale.title'>Register your first ammo sale</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.ammoSale.line1'>Open «Ammo-salg» and click «Registrer ammo-salg».</Text>,
            <Text key='line2' id='clubOps.gettingStarted.ammoSale.line2'>Pick the member, the product and the quantity, and choose the payment method.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.ammoSale.line3'>Stock and the member's purchase history are updated automatically.</Text>,
        ],
    },
    {
        key: 'course',
        icon: 'fa-solid fa-graduation-cap',
        href: '/course',
        title: <Text id='clubOps.gettingStarted.course.title'>Publish a course</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.course.line1'>Open «Kurs» and create a new course, e.g. a safety course for new members.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.course.line2'>Set date, price and the number of seats – members sign up and pay themselves.</Text>,
        ],
    },
    {
        key: 'shotTrainer',
        icon: 'fa-solid fa-bullseye',
        href: '/shot-trainer',
        title: <Text id='clubOps.gettingStarted.shotTrainer.title'>Try the shot trainer</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.shotTrainer.line1'>Open «Trener» – a command timer with light signals for felt and NAIS training.</Text>,
            <Text key='line2' id='clubOps.gettingStarted.shotTrainer.line2'>Pick an exercise, set shooting and loading time, and press start.</Text>,
            <Text key='line3' id='clubOps.gettingStarted.shotTrainer.line3'>Show it to the members – it also works on the phone at the range.</Text>,
        ],
    },
    {
        key: 'ballisticCalc',
        icon: 'fa-solid fa-calculator',
        href: '/ballistic-calculator',
        title: <Text id='clubOps.gettingStarted.ballisticCalc.title'>Try the ballistic calculator</Text>,
        lines: [
            <Text key='line1' id='clubOps.gettingStarted.ballisticCalc.line1'>Open «Ballistikk-kalkulator».</Text>,
            <Text key='line2' id='clubOps.gettingStarted.ballisticCalc.line2'>Enter the load data (ballistic coefficient and muzzle velocity) to get drop and drift per distance.</Text>,
        ],
    },
];

const GROUPS = [
    {
        key: 'frontpage',
        steps: ['frontpageArticle', 'feedPost'],
        title: <Text id='clubOps.gettingStarted.group.frontpage'>Frontpage and feed</Text>,
    },
    {
        key: 'conventions',
        steps: ['training', 'competition', 'results'],
        title: <Text id='clubOps.gettingStarted.group.conventions'>Conventions and trainings</Text>,
    },
    {
        key: 'policeForm',
        steps: ['policeForm', 'policeFormSigned'],
        title: <Text id='clubOps.gettingStarted.group.policeForm'>Police form and signing</Text>,
    },
    {
        key: 'calendar',
        steps: ['calendar', 'calendarEvent'],
        title: <Text id='clubOps.gettingStarted.group.calendar'>Club calendar</Text>,
    },
    {
        key: 'clubOps',
        steps: ['weapons', 'ranges', 'rangeOfficers', 'keys'],
        title: <Text id='clubOps.gettingStarted.group.clubOps'>Club operations</Text>,
    },
    {
        key: 'modules',
        steps: ['ammoSale', 'course', 'shotTrainer', 'ballisticCalc'],
        title: <Text id='clubOps.gettingStarted.group.modules'>Other modules</Text>,
    },
];

const STEP_BY_KEY = {};
STEPS.forEach(step => {
    STEP_BY_KEY[step.key] = step;
});

/**
 * «Kom i gang» card for shooting clubs on the frontpage. Same mechanics as
 * the Dyrejournal card: auto-detected steps from
 * GET /api/customers/club-getting-started, manual overrides and dismissal
 * stored on customer.gettingStartedOverrides / gettingStartedDismissed.
 * Everything is scoped to the DOMAIN customer (appState.currentCustomer, the
 * club site the user is on), never userStore.customer: that is the user's
 * switched-to / personal customer and on a club site it may be another club.
 * Rendered for club admins and range officers only (the render site in
 * routes/frontpage/loggedIn.js gates on currentCustomer.menuItems.clubOperations).
 */
@observer
class ClubGettingStarted extends Component {
    constructor(props) {
        super(props);
        this.state = {
            apiSteps: {},
            expandedKey: null,
        };
    }

    componentDidMount() {
        const { appState } = this.props.stores;
        const dismissed = parseInt(appState.currentCustomer?.gettingStartedDismissed, 10) === 1;
        if (!dismissed && this.canSee()) {
            this.loadStatus();
        }
    }

    canSee = () => {
        const { userStore } = this.props.stores;
        return !!(userStore.isAdmin || userStore.isCustomerAdmin || userStore.isRangeOfficer);
    }

    isClubAdmin = () => {
        const { userStore } = this.props.stores;
        return !!(userStore.isAdmin || userStore.isCustomerAdmin);
    }

    loadStatus = async () => {
        const { appState } = this.props.stores;
        // The backend resolves the club from the hostname; the explicit id is
        // the fallback for localhost, where hostname resolution yields nothing.
        const customer = parseInt(appState.currentCustomer?.id, 10) || 0;
        const response = await util.fetchApi('/api/customers/club-getting-started', { method: 'GET' }, customer > 0 ? { customer } : {});
        if (response.status === 200 && response.data) {
            this.setState({ apiSteps: response.data.steps || {} });
        }
    }

    toggleExpanded = (key) => {
        this.setState(prevState => ({
            expandedKey: prevState.expandedKey === key ? null : key,
        }));
    }

    toggleOverride = async (key) => {
        const { customerStore, appState } = this.props.stores;
        const { currentCustomer: customer = {} } = appState;
        const current = customer.gettingStartedOverrides || [];
        const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
        const response = await customerStore.saveField(customer.id, 'gettingStartedOverrides-overwrite', next, false);
        if (response && response.status < 300) {
            appState.updateObjectKeyValue('currentCustomer', 'gettingStartedOverrides', next);
        }
    }

    setDismissed = async (value) => {
        const { customerStore, appState } = this.props.stores;
        const { currentCustomer: customer = {} } = appState;
        const response = await customerStore.saveField(customer.id, 'gettingStartedDismissed', value, false);
        if (response && response.status < 300) {
            appState.updateObjectKeyValue('currentCustomer', 'gettingStartedDismissed', value);
            if (value === 0) {
                this.loadStatus();
            }
        }
    }

    goTo = (step) => {
        if (step.drawer === 'newArticle') {
            // Same drawer the «+ Frontpage» button opens (article/list.js
            // onClickNewArticle): a main article has no category.
            const { drawerLevel = 1 } = this.props;
            const { appState } = this.props.stores;
            appState.openDrawer('newArticle', {
                height: appState.drawerHeightMediumLarge,
                isNew: true,
                category: '',
                active: 1,
                callback: () => this.loadStatus(),
            }, drawerLevel + 1);
            return;
        }
        if (step.drawer === 'newCalendar') {
            // The calendar page has no «new calendar» button; open the existing
            // drawer directly, scoped to the DOMAIN club (post-calendar would
            // otherwise default customer to currentUser.customer).
            const { drawerLevel = 1 } = this.props;
            const { appState, calendarStore } = this.props.stores;
            const customerId = parseInt(appState.currentCustomer?.id, 10);
            calendarStore.updateKeyValue('newCalendar', customerId > 0 ? { customer: customerId } : {});
            appState.openDrawer('newCalendar', {
                height: appState.drawerHeightMedium,
                callback: () => this.loadStatus(),
            }, drawerLevel + 1);
            return;
        }
        route(step.href);
    }

    renderStep = (step, state, expandedKey, isClubAdmin) => {
        const isExpanded = expandedKey === step.key;
        let iconClass = 'fa-regular fa-circle text-body-secondary';
        if (state.done) {
            iconClass = state.overridden ? 'fa-solid fa-circle-check text-secondary' : 'fa-solid fa-circle-check text-success';
        }
        return (
            <Fragment key={step.key}>
                <button
                    type='button'
                    class='list-group-item list-group-item-action d-flex align-items-center'
                    onClick={() => this.toggleExpanded(step.key)}
                >
                    <i class={`${iconClass} me-2`} />
                    <i class={`${step.icon} me-2 text-body-secondary`} />
                    <span class='flex-grow-1 text-start'>{step.title}</span>
                    <i class={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-body-secondary ms-2`} />
                </button>
                {isExpanded && (
                    <div class='list-group-item bg-body-secondary'>
                        <ol class='mb-2'>
                            {step.lines.map((line, idx) => (
                                <li key={idx} class='mb-1'>{line}</li>
                            ))}
                        </ol>
                        <div class='d-flex align-items-center justify-content-between'>
                            <button type='button' class='btn btn-sm btn-primary' onClick={() => this.goTo(step)}>
                                <i class='fa-duotone fa-arrow-right me-1' />
                                <Text id='clubOps.gettingStarted.goThere'>Take me there</Text>
                            </button>
                            {isClubAdmin && (!state.done || state.overridden) && (
                                <button
                                    type='button'
                                    class='btn btn-sm btn-link text-body-secondary'
                                    onClick={() => this.toggleOverride(step.key)}
                                >
                                    {state.overridden ? (
                                        <Text id='clubOps.gettingStarted.unmarkDone'>Remove manual completion</Text>
                                    ) : (
                                        <Text id='clubOps.gettingStarted.markDone'>Mark as done</Text>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </Fragment>
        );
    }

    render() {
        const { appState } = this.props.stores;
        const { currentCustomer: customer = {} } = appState;
        if (!this.canSee()) {
            return '';
        }
        const isClubAdmin = this.isClubAdmin();
        const dismissed = parseInt(customer.gettingStartedDismissed, 10) === 1;

        if (dismissed) {
            if (!isClubAdmin) {
                return '';
            }
            return (
                <div class='text-center mb-3'>
                    <button
                        type='button'
                        class='btn btn-sm btn-link text-body-secondary'
                        onClick={() => this.setDismissed(0)}
                    >
                        <i class='fa-duotone fa-circle-question me-1' />
                        <Text id='clubOps.gettingStarted.show'>Show the Getting started guide</Text>
                    </button>
                </div>
            );
        }

        const { apiSteps, expandedKey } = this.state;
        const overrides = customer.gettingStartedOverrides || [];
        const stepStates = computeClubStepStates(apiSteps, overrides);
        const stateByKey = {};
        stepStates.forEach(s => {
            stateByKey[s.key] = s;
        });
        const doneCount = countDone(stepStates);
        const total = stepStates.length;
        const allDone = doneCount === total;
        // Collapsed by default (unlike the Dyrejournal card): the one-line
        // header with progress stays; «Open» expands the list for this view.
        const collapsed = this.state.collapsed !== undefined ? this.state.collapsed : true;

        const progressText = (
            <Text id='clubOps.gettingStarted.progress' fields={{ done: doneCount, total }}>
                {doneCount} of {total} completed
            </Text>
        );

        if (collapsed) {
            return (
                <div class='card mb-3 rounded-lg'>
                    <div class='card-body py-2 d-flex align-items-center justify-content-between'>
                        <span>
                            <i class={`me-2 ${allDone ? 'fa-solid fa-circle-check text-success' : 'fa-duotone fa-rocket-launch'}`} />
                            <Text id='clubOps.gettingStarted.title'>Getting started with the club</Text>
                            <small class='text-body-secondary ms-2'>{progressText}</small>
                        </span>
                        <button type='button' class='btn btn-sm btn-link' onClick={() => this.setState({ collapsed: false })}>
                            <Text id='clubOps.gettingStarted.open'>Open</Text>
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div class='card mb-3 rounded-lg'>
                <div class='card-body pb-2'>
                    <div class='d-flex align-items-center justify-content-between'>
                        <h6 class='mb-0'>
                            <i class='fa-duotone fa-rocket-launch me-2' />
                            <Text id='clubOps.gettingStarted.title'>Getting started with the club</Text>
                        </h6>
                        <button
                            type='button'
                            class='btn btn-sm btn-link text-body-secondary'
                            onClick={() => this.setState({ collapsed: true })}
                        >
                            <i class='fa-solid fa-chevron-up' />
                        </button>
                    </div>
                    <small class='text-body-secondary'>{progressText}</small>
                    <div class='progress mt-1 mb-2' style='height: 6px;'>
                        <div
                            class={`progress-bar ${allDone ? 'bg-success' : 'bg-info'}`}
                            style={`width: ${Math.round((doneCount / total) * 100)}%`}
                        />
                    </div>
                    {allDone && (
                        <p class='text-body-secondary mb-2'>
                            <i class='fa-solid fa-circle-check text-success me-2' />
                            <Text id='clubOps.gettingStarted.allDone'>All done – great job!</Text>
                        </p>
                    )}
                </div>
                {GROUPS.map(group => (
                    <Fragment key={group.key}>
                        <div class='px-3 py-1 bg-body-secondary border-top'>
                            <small class='fw-bold text-body-secondary text-uppercase'>{group.title}</small>
                        </div>
                        <div class='list-group list-group-flush'>
                            {group.steps.map(key => this.renderStep(STEP_BY_KEY[key], stateByKey[key], expandedKey, isClubAdmin))}
                        </div>
                    </Fragment>
                ))}
                {isClubAdmin && (
                    <div class='card-body py-2 text-end'>
                        <button
                            type='button'
                            class='btn btn-sm btn-link text-body-secondary'
                            onClick={() => this.setDismissed(1)}
                        >
                            <i class='fa-duotone fa-eye-slash me-1' />
                            <Text id='clubOps.gettingStarted.dismiss'>Hide for the club</Text>
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

export default ClubGettingStarted;
